import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { jwtVerify, SignJWT } from "jose";
import fetchWithCache from "./cache";
import {
  type ClassroomSeatingResponse,
  type NextAvailableCourseResponse,
  type SuccessResponse,
  type ClassroomSeatingData,
} from "../types/middleware";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export async function generateNonceJWT(
  secret: string,
  purpose: string
): Promise<string> {
  const key = new TextEncoder().encode(secret);
  const jwt = await new SignJWT({ purpose: purpose })
    .setProtectedHeader({ alg: "HS256" })
    .setIssuedAt()
    .setExpirationTime("24h") // Set an expiration time for the nonce
    .sign(key);
  return jwt;
}

export async function verifyNonceJWT(
  nonce: string,
  secret: string,
  purpose: string
): Promise<boolean> {
  const key = new TextEncoder().encode(secret);
  try {
    const { payload } = await jwtVerify(nonce, key);
    return payload.purpose === purpose;
  } catch {
    return false;
  }
}

export function uint8ToBase64(u8: Uint8Array) {
  let bin = "";
  for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i]);
  return btoa(bin);
}

export async function verifyContext(env: any, userEmail: any) {
  if (!userEmail) return null;

  try {
    const API_URL = `${env.MIDDLEWARE_URL}/verifyContext`;
    const ACCESS_TOKEN = env.MIDDLEWARE_LIMITED_KEY;

    const url = new URL(API_URL);
    url.searchParams.set("API_LIMITED_AUTH_KEY", ACCESS_TOKEN);

    const headers = {
      "Content-Type": "application/json",
    };

    const request = new Request(url.toString(), {
      method: "POST",
      headers: headers,
      body: JSON.stringify({ email: userEmail }),
    });

    // Cache the response
    const resp = await fetchWithCache.fetch(request);

    if (!resp.ok) {
      const errorText = await resp.text();
      console.error("[verifyContext] API error:", resp.status, errorText);
      throw new Error(`verifyContext failed: ${resp.status} - ${errorText}`);
    }

    const userData: SuccessResponse = await resp.json();
    return userData;
  } catch (error: any) {
    console.error("[verifyContext] Exception:", error?.message || error);
    return null;
  }
}

export async function getNextCourseInstances(
  env: any,
  methodRecordId: any,
  courseTypeId: any
) {
  if (methodRecordId) {
    const API_URL = `${env.MIDDLEWARE_URL}/limited/customer/${methodRecordId}/course/${courseTypeId}`;
    const ACCESS_TOKEN = env.MIDDLEWARE_LIMITED_KEY;

    const url = new URL(API_URL);
    url.searchParams.set("API_LIMITED_AUTH_KEY", ACCESS_TOKEN);

    const request = new Request(url.toString(), {
      method: "GET",
      // headers: headers,
    });

    // Cache the response
    const resp = await fetchWithCache.fetch(request);

    // Parse and log the JSON
    const courseData: NextAvailableCourseResponse = await resp.json();

    if (
      courseData.success !== true ||
      !courseData?.data?.customerCourseDetailsData
    ) {
      console.error(
        "[getNextCourseInstances] API returned an error:",
        courseData
      );
      return null;
    }
    return courseData.data.customerCourseDetailsData;
  } else {
    return null;
  }
}

export async function getCourseInstanceSeats(
  env: any,
  courseInstanceRecordId: any,
  userRecordId: any,
  categoryRecordId: any
) {
  if (courseInstanceRecordId && userRecordId && categoryRecordId) {
    const API_URL = `${env.MIDDLEWARE_URL}/limited/customer/${userRecordId}/course/${courseInstanceRecordId}/category/${categoryRecordId}/seatingchart`;
    const ACCESS_TOKEN = env.MIDDLEWARE_LIMITED_KEY;

    const url = new URL(API_URL);
    url.searchParams.set("API_LIMITED_AUTH_KEY", ACCESS_TOKEN);

    const request = new Request(url.toString(), {
      method: "GET",
      // headers: headers,
    });

    // Cache the response
    const resp = await fetchWithCache.fetch(request);

    // Parse and log the JSON
    const seatData: ClassroomSeatingResponse = await resp.json();

    if (seatData.success !== true || !seatData?.data) {
      console.error(
        "[getCourseInstanceSeats] API returned an error:",
        seatData
      );
      return null;
    }
    return seatData.data as ClassroomSeatingData;
  } else {
    return null;
  }
}
